import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border border-transparent px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        // Order status badges
        "status-open":
          "rounded-sm bg-linear-to-br from-[#28a745] to-[#20c997] text-white border-[#1e7e34] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "status-pending":
          "rounded-sm bg-linear-to-br from-[#ffc107] to-[#fd7e14] text-white border-[#d39e00] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "status-released":
          "rounded-sm bg-linear-to-br from-[#17a2b8] to-[#20c997] text-white border-[#117a8b] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "status-invoiced":
          "rounded-sm bg-linear-to-br from-[#667eea] to-[#764ba2] text-white border-[#5a67d8] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "status-cancelled":
          "rounded-sm bg-linear-to-br from-[#dc3545] to-[#c82333] text-white border-[#bd2130] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        // Order type / storage badges
        "type-frozen":
          "rounded-sm bg-linear-to-br from-[#007bff] to-[#0056b3] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "type-chilled":
          "rounded-sm bg-linear-to-br from-[#17a2b8] to-[#20c997] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "type-ambient":
          "rounded-sm bg-linear-to-br from-[#9ca3af] to-[#6b7280] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "type-mixed":
          "rounded-sm bg-[linear-gradient(90deg,#17a2b8_50%,#007bff_50%)] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
        "type-na":
          "rounded-sm bg-linear-to-br from-[#f59e0b] to-[#d97706] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-xs max-md:px-2.5 max-md:py-1",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
